import { LoggingService } from "../../ServiceDesk/sdServices/loggingSvc";
import { FlightingService } from "../../ServiceDesk/sdServices/flightingSvc";
import { TelemetryServiceImplementation } from "./telemetry-service";

export interface PerformanceResourceTelemetry {
  /**
   * Name resource type
   */
  type: string;
  /**
   * url
   */
  url: string;
  /**
   * time to first byte in milliseconds
   */
  timeToFirstByte: number;
  /**
   * dnsTime in milliseconds
   */
  dnsTime: number;
  /**
   * stall Time in milliseconds
   */
  tcpHandshakeTime: number;
  /**
   * tcp Handshake Time in milliseconds
   */
  stallTime: number;
  /**
   * response Time in milliseconds
   */
  responseTime: number;
  /**
   * total duration milliseconds
   */
  duration: number;
  /**
   * total transferSize in byte
   */
  transferSize: number;
}

// list of URLs for which we don't want to log resource-timing info
const URLS_TO_SKIP: string[] = ["https://web.vortex"];

const RESOURCE_TIMING_ID = "Performance-Resource-Timing";
// flight to enable resource timing logging
const SD_RESOURCE_TIMING_LOGGING = "SD_RESOURCE_TIMING_LOGGING";
// flight to enable resource timing logging
const SD_RESOURCE_OBSERVER = "SD_RESOURCE_OBSERVER";

export class ResourceTimingLoggerService {
  private totalTransferSize = 0;
  private totalResponseTime = 0;
  private debugLogsEnabled = false;
  private logTelemetry = false;
  private perfObserver: any = undefined;
  // @ngInject
  constructor(
    private loggingService: LoggingService,
    private telemetryService: TelemetryServiceImplementation,
    private flightingService: FlightingService,
    private $window: ng.IWindowService
  ) {}

  public Initialize(): void {
    try {
      if (!this.perfObserver) {
        // to show console log, please add debug=true on querystring.
        if (this.$window.location && this.$window.location.hash && this.$window.location.href.includes("debug=true")) {
          this.debugLogsEnabled = true;
        }
        // only log events/errors to xper if this flight is enable
        this.logTelemetry = this.flightingService.isFlightEnabled(SD_RESOURCE_TIMING_LOGGING);

        // create performance observe
        if (this.flightingService.isFlightEnabled(SD_RESOURCE_OBSERVER)) {
          this.createPerformanceObserver();
        }
      }
    } catch (error) {
      if (this.logTelemetry) {
        this.loggingService.logInfo("Failed to initialize resource-timing logger. Not supported by IE/Edge");
      }
      return;
    }
  }

  // create observer for resource performance event types
  private createPerformanceObserver(): void {
    // Create observer for all performance event types
    this.perfObserver = new this.$window.PerformanceObserver(this.ObserverCallback.bind(this));
    this.perfObserver.observe({ entryTypes: ["resource"] });
  }

  private ObserverCallback(performanceEntryList: any): void {
    try {
      if (!performanceEntryList) {
        return;
      }

      let entries = performanceEntryList.getEntriesByType("resource");
      let reCalculate = false;
      for (let i = 0; i < entries.length; i++) {
        let entry: any = entries[i];
        // if the resource is fetched from a local cache, or if it is a cross-origin resource(), transferSize = 0.
        // need to add Timing-Allow-Origin on the response header for cross domain api calls to get the data.
        // no need to log resource that doesn't have enough data.
        if (
          !this.ShouldLog(entry) ||
          !entry.duration ||
          entry.duration === 0 ||
          !entry.transferSize ||
          entry.transferSize === 0 ||
          !entry.responseStart ||
          entry.responseStart === 0 ||
          !entry.responseEnd ||
          entry.responseEnd === 0
        ) {
          continue;
        }
        // ref: https://nicj.net/resourcetiming-in-practice/#blocking-time
        let stallTime = 0;
        if (entry.connectEnd && entry.connectEnd === entry.fetchStart) {
          stallTime = entry.requestStart - entry.connectEnd;
        } else if (entry.domainLookupStart) {
          stallTime = entry.domainLookupStart - entry.fetchStart;
        }

        // ref: https://docs.w3cub.com/dom/performanceresourcetiming/
        let info: PerformanceResourceTelemetry = {
          type: RESOURCE_TIMING_ID,
          url: entry.name,
          timeToFirstByte: this.LowerBound(0, entry.responseStart - entry.startTime),
          dnsTime: this.LowerBound(0, entry.domainLookupEnd - entry.domainLookupStart),
          stallTime: stallTime,
          tcpHandshakeTime: this.LowerBound(0, entry.connectEnd - entry.connectStart),
          responseTime: entry.responseEnd - entry.responseStart,
          transferSize: entry.transferSize,
          duration: entry.duration
        };
        // set running totals
        this.totalTransferSize += info.transferSize;
        this.totalResponseTime += info.responseTime;
        // enable debug mode
        if (this.debugLogsEnabled) {
          console.log(`PerformanceObserver: ${JSON.stringify(info)}`);
        }
        // log to xpert if this is enable
        if (this.logTelemetry) {
          this.loggingService.logInfo(info);
        }
        reCalculate = true;
      }

      if (reCalculate) {
        let transferRate = this.calculateKbPerSecond(this.totalTransferSize, this.totalResponseTime);
        this.telemetryService.setDataTransferRate(transferRate);
        // enable debug mode
        if (this.debugLogsEnabled) {
          console.log(`PerformanceObserver: dataTransferRate in KbPerSecond = ${transferRate}`);
        }
      }
    } catch (error) {
      if (this.debugLogsEnabled) {
        console.log(`createPerformanceObserver error ${error}`);
      }
    }
  }
  // calculate network speed- kb per second based on totalTransferSize and totalResponseTime
  private calculateKbPerSecond(transferSize: number, responseTime: number): number {
    // totalTransferSize / totalResponseTime would be bytes per millisecond
    // 1 second = 1000 milliseconds. 1024 bytes = 1kb
    if (transferSize > 0 && responseTime > 0) {
      return ((transferSize / responseTime) * 1000) / 1024;
    }

    return 0;
  }

  // log only if the url is a SD or SD partner service uri
  private ShouldLog(entry: PerformanceResourceTiming): boolean {
    if (!entry || !entry.name || !URLS_TO_SKIP.length) {
      return false;
    }

    for (let i = 0; i < URLS_TO_SKIP.length; i++) {
      if (entry.name.toLowerCase().startsWith(URLS_TO_SKIP[i])) {
        return false;
      }
    }
    return true;
  }

  private LowerBound(lowerBound: number, value: number): number {
    return value < lowerBound ? lowerBound : value;
  }
}
